import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Clapperboard,
  Loader2,
  ArrowUp,
  ArrowDown,
  Trash2,
  FileVideo,
  Upload,
  CheckCircle2,
  FolderUp,
} from 'lucide-react'

import { dramaApi, dramaQueryKeys } from '@/lib/api/drama'
import type { DramaProjectStatus } from '@/types/drama'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/toaster'
import { formatDate } from '@/lib/utils'
import { dramaT } from '@/components/drama/i18n'
import { useDramaLang } from '@/lib/useDramaLang'
import { LangToggle } from '@/components/drama/LangToggle'

const STATUS_VARIANT: Record<DramaProjectStatus, 'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'info'> = {
  draft: 'secondary',
  uploading: 'warning',
  preprocessing: 'warning',
  ready: 'success',
  failed: 'destructive',
  archived: 'outline' as 'secondary',
}

interface PendingEpisode {
  file: File
  /** Path including folder (e.g. "season1/EP01.mp4") when the file came from a
   *  folder pick or a folder drag-drop. Used to display origin and to sort
   *  files in natural order before assigning episode numbers. */
  path?: string
  uploaded: number  // bytes uploaded so far, used during the per-file upload phase
  status: 'queued' | 'uploading' | 'done' | 'failed'
  error?: string
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
}

// Folder uploads frequently arrive with empty MIME types (especially from
// macOS Finder via the File System Access fallback path), so fall back to
// extension matching alongside the `video/*` check.
const VIDEO_EXTS = ['.mp4', '.mov', '.m4v', '.mkv', '.webm', '.avi']

function isVideoFile(f: File): boolean {
  if (f.type.startsWith('video/')) return true
  const lower = f.name.toLowerCase()
  return VIDEO_EXTS.some((ext) => lower.endsWith(ext))
}

// Natural-order comparator so "EP02.mp4" beats "EP10.mp4" the way humans expect.
function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
}

/** Recursively walk a list of dropped entries, returning every file with its
 *  fullPath (e.g. "Drama1/Episode01/clip.mp4"). Uses the legacy
 *  webkitGetAsEntry / FileSystemDirectoryEntry API — broadly supported in
 *  Chromium-based browsers and Safari; the caller handles browsers without it. */
async function collectFromEntries(entries: FileSystemEntry[]): Promise<{ file: File; path: string }[]> {
  const out: { file: File; path: string }[] = []
  async function walk(entry: FileSystemEntry): Promise<void> {
    if (entry.isFile) {
      const fe = entry as FileSystemFileEntry
      await new Promise<void>((resolve) => {
        fe.file(
          (f) => {
            out.push({ file: f, path: entry.fullPath.replace(/^\//, '') })
            resolve()
          },
          () => resolve(),
        )
      })
    } else if (entry.isDirectory) {
      const de = entry as FileSystemDirectoryEntry
      const reader = de.createReader()
      // readEntries returns at most ~100 entries per call; loop until empty.
      while (true) {
        const batch: FileSystemEntry[] = await new Promise((resolve) =>
          reader.readEntries((es) => resolve(es), () => resolve([])),
        )
        if (batch.length === 0) break
        for (const child of batch) await walk(child)
      }
    }
  }
  for (const e of entries) await walk(e)
  return out
}

export function DramaProjectsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [lang, setLang] = useDramaLang()
  const t = dramaT(lang)

  const { data, isLoading } = useQuery({
    queryKey: dramaQueryKeys.list(),
    queryFn: () => dramaApi.listProjects(),
    refetchInterval: (q) => {
      const anyActive = q.state.data?.items?.some(
        (i) => i.status === 'preprocessing' || i.status === 'uploading',
      )
      return anyActive ? 3000 : false
    },
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Clapperboard className="w-7 h-7 text-spectrum-magenta" />
          <div>
            <h1 className="text-2xl font-semibold">Drama Mix Projects</h1>
            <p className="text-sm text-muted-foreground">
              {t('proj.subtitle')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <LangToggle lang={lang} onChange={setLang} />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              {t('proj.new')}
            </Button>
          </DialogTrigger>
          <CreateProjectDialog
            onClose={() => setOpen(false)}
            onCreated={(projectId) => {
              queryClient.invalidateQueries({ queryKey: dramaQueryKeys.list() })
              setOpen(false)
              navigate(`/drama/${projectId}`)
            }}
          />
        </Dialog>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : !data || data.items.length === 0 ? (
        <div className="text-center py-24 border-2 border-dashed rounded-lg">
          <Clapperboard className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">{t('proj.empty')}</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {data.items.map((p) => (
            <button
              key={p.id}
              onClick={() => navigate(`/drama/${p.id}`)}
              className="text-left p-4 rounded-lg border bg-card hover:bg-accent/30 transition-colors flex items-center justify-between"
            >
              <div>
                <div className="font-semibold mb-1">{p.title}</div>
                <div className="text-xs text-muted-foreground">
                  {p.language?.toUpperCase()} · {t('proj.updatedAt', formatDate(p.updated_at))}
                </div>
              </div>
              <Badge variant={STATUS_VARIANT[p.status] ?? 'secondary'}>{p.status}</Badge>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

interface CreateDialogProps {
  onCreated: (projectId: string) => void
  onClose: () => void
}

/**
 * Two-phase Create modal:
 *  1) Compose phase — user enters title, language, and selects/reorders mp4 files
 *  2) Upload phase — disable inputs, create the project, then sequentially upload
 *     each file (assigning episode_number from list order).
 *
 * We keep both phases in one dialog so the user can watch progress without
 * losing the file list (reopening the dialog would forget the picker state).
 */
function CreateProjectDialog({ onCreated, onClose }: CreateDialogProps) {
  const [lang] = useDramaLang()
  const t = dramaT(lang)
  const filesInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement | null>(null)
  const [title, setTitle] = useState('')
  const [language, setLanguage] = useState('en')
  const [episodes, setEpisodes] = useState<PendingEpisode[]>([])
  const [phase, setPhase] = useState<'compose' | 'running'>('compose')
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // `webkitdirectory` is a non-standard attribute that React typings don't
  // know about; we set it on the DOM node so the folder picker (not a plain
  // file picker) appears. This MUST run via a callback ref, not a mount-time
  // useEffect: the input lives inside the Radix Dialog portal, which only
  // mounts the node when the dialog opens — long after this component first
  // mounts. A useEffect([]) fires while the node is still null, bails, and
  // never re-runs, so the attribute would never get set. The callback ref
  // fires every time the node actually attaches.
  const setupFolderInput = useCallback((el: HTMLInputElement | null) => {
    folderInputRef.current = el
    if (!el) return
    el.setAttribute('webkitdirectory', '')
    el.setAttribute('directory', '')
  }, [])

  function resetInputs() {
    if (filesInputRef.current) filesInputRef.current.value = ''
    if (folderInputRef.current) folderInputRef.current.value = ''
  }

  function appendItems(items: { file: File; path?: string }[], sort: boolean) {
    const videos = items.filter((it) => isVideoFile(it.file))
    const skipped = items.length - videos.length
    if (videos.length === 0) {
      toast({
        title: t('proj.noVideosTitle'),
        description: skipped > 0 ? t('proj.noVideosInFolder') : t('proj.nothingSelected'),
        variant: 'destructive',
      })
      return
    }
    if (sort) {
      videos.sort((a, b) => naturalCompare(a.path || a.file.name, b.path || b.file.name))
    }
    // Friendlier confirmation: break the count down by top-level folder, so
    // the user can verify what just got picked up (especially helpful when
    // they're chaining multiple folder picks).
    const folderCounts = new Map<string, number>()
    for (const v of videos) {
      const top = v.path?.split('/')[0] ?? t('proj.filesLabel')
      folderCounts.set(top, (folderCounts.get(top) ?? 0) + 1)
    }
    const folderSummary = Array.from(folderCounts.entries())
      .map(([name, n]) => `${name} (${n})`)
      .join(' · ')
    toast({
      title: t('proj.addedN', videos.length),
      description: folderSummary + (skipped > 0 ? t('proj.skippedN', skipped) : ''),
    })
    setEpisodes((prev) => [
      ...prev,
      ...videos.map((it) => ({
        file: it.file,
        path: it.path,
        uploaded: 0,
        status: 'queued' as const,
      })),
    ])
  }

  function addFromFileList(list: FileList | null) {
    if (!list || list.length === 0) return
    const items = Array.from(list).map((f) => ({
      file: f,
      // Folder picks set webkitRelativePath (e.g. "Drama1/EP01.mp4"); flat
      // file picks leave it empty.
      path: (f as any).webkitRelativePath ? (f as any).webkitRelativePath as string : undefined,
    }))
    // Sort only when the pick actually carries folder structure — flat file
    // selections preserve user-selected order, matching prior behaviour.
    const hasFolder = items.some((it) => !!it.path)
    appendItems(items, hasFolder)
    resetInputs()
  }

  // Native file dialogs on macOS / Windows physically cannot multi-select
  // directories — neither `<input webkitdirectory>` nor showDirectoryPicker
  // support it. So adding multiple folders means one of:
  //   (a) pick a parent containing your subfolders — webkitRelativePath
  //       carries the tree, we recurse.
  //   (b) click "添加文件夹" again for each additional folder.
  //   (c) drop multiple folders onto the list — already handled in handleDrop.
  // [FIX-2026-06-06] No auto-reopen chain: it re-popped the OS picker after every
  // pick and relied on an unreliable `cancel` event to stop, which surprised
  // users ("why does the picker keep opening?"). One click = one folder.
  function openFolderPicker() {
    folderInputRef.current?.click()
  }

  function handleFolderInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const input = e.target
    const list = input.files
    if (!list || list.length === 0) return
    addFromFileList(list)
    input.value = ''
  }

  async function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    if (phase === 'running') return
    const dt = e.dataTransfer
    // Prefer the DataTransferItem path so we can recurse into directories.
    const itemList = dt.items
    if (itemList && itemList.length > 0 && typeof itemList[0]?.webkitGetAsEntry === 'function') {
      const entries: FileSystemEntry[] = []
      for (let i = 0; i < itemList.length; i++) {
        const entry = itemList[i].webkitGetAsEntry?.()
        if (entry) entries.push(entry)
      }
      if (entries.length > 0) {
        const collected = await collectFromEntries(entries)
        const sawFolder = entries.some((en) => en.isDirectory)
        // When folders are involved, sort the whole batch naturally across
        // folder boundaries (folder name first, then filename). For pure
        // file drops, keep the drop order.
        appendItems(collected, sawFolder)
        return
      }
    }
    addFromFileList(dt.files)
  }

  function move(idx: number, dir: -1 | 1) {
    const target = idx + dir
    if (target < 0 || target >= episodes.length) return
    setEpisodes((prev) => {
      const next = [...prev]
      ;[next[idx], next[target]] = [next[target], next[idx]]
      return next
    })
  }

  function remove(idx: number) {
    setEpisodes((prev) => prev.filter((_, i) => i !== idx))
  }

  async function handleSubmit() {
    if (!title.trim() || episodes.length === 0) return
    setPhase('running')
    setSubmitError(null)

    let projectId = createdProjectId
    try {
      if (!projectId) {
        const project = await dramaApi.createProject({ title: title.trim(), language })
        projectId = project.id
        setCreatedProjectId(projectId)
      }

      // Upload in parallel with a bounded pool. The client uplink is the
      // bottleneck for 100-300MB clips; 5 concurrent transfers saturate it far
      // better than one-at-a-time. Episode number stays index-based (idx+1) so
      // order is preserved regardless of finish order. A single failure no longer
      // aborts the rest — the others finish, failed ones are marked, and we
      // surface the first error after the batch settles (the pool skips
      // already-'done' files, so a retry resumes where it left off).
      const UPLOAD_CONCURRENCY = 5
      const targetProjectId = projectId as string // guaranteed set by the create step above
      const pending = episodes
        .map((e, idx) => ({ e, idx }))
        .filter(({ e }) => e.status !== 'done')
      let cursor = 0
      let firstError: any = null

      const worker = async () => {
        while (cursor < pending.length) {
          const { idx } = pending[cursor++]
          setEpisodes((prev) => prev.map((e, k) => (k === idx ? { ...e, status: 'uploading' } : e)))
          try {
            await dramaApi.uploadEpisode(
              targetProjectId,
              idx + 1,
              episodes[idx].file,
              language,
              (loaded, total) => {
                setEpisodes((prev) =>
                  prev.map((e, k) =>
                    k === idx ? { ...e, uploaded: total > 0 ? loaded / total : 0 } : e,
                  ),
                )
              },
            )
            setEpisodes((prev) =>
              prev.map((e, k) => (k === idx ? { ...e, status: 'done', uploaded: 1 } : e)),
            )
          } catch (err: any) {
            const msg = err?.response?.data?.detail || err?.message || 'upload failed'
            setEpisodes((prev) =>
              prev.map((e, k) => (k === idx ? { ...e, status: 'failed', error: msg } : e)),
            )
            if (!firstError) firstError = err
          }
        }
      }

      await Promise.all(
        Array.from({ length: Math.min(UPLOAD_CONCURRENCY, pending.length) }, () => worker()),
      )
      if (firstError) throw firstError

      toast({
        title: t('proj.created'),
        description: t('proj.createdDesc', episodes.length),
      })
      onCreated(projectId)
    } catch (e: any) {
      setSubmitError(e?.response?.data?.detail || e?.message || t('proj.genericError'))
      // Keep dialog open so the user can retry or remove the failed file
    }
  }

  const completed = episodes.filter((e) => e.status === 'done').length
  const running = phase === 'running'
  // Surface exactly why the submit button is disabled so users aren't left
  // staring at a greyed-out button wondering what they missed.
  const blockers: string[] = []
  if (!title.trim()) blockers.push(t('proj.needTitle'))
  if (episodes.length === 0) blockers.push(t('proj.needFile'))
  const canSubmit = !running && blockers.length === 0

  return (
    <DialogContent className="sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle>{t('proj.createTitle')}</DialogTitle>
        <DialogDescription>{t('proj.createDesc')}</DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-2">
        <div className="grid grid-cols-[1fr_120px] gap-3">
          <div>
            <Label htmlFor="title">{t('proj.title')}</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="My Destined Mate"
              disabled={running}
            />
          </div>
          <div>
            <Label htmlFor="language">{t('proj.language')}</Label>
            <Input
              id="language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              placeholder="en"
              disabled={running}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2 gap-2">
            <Label className="shrink-0">{t('proj.episodesN', episodes.length)}</Label>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => filesInputRef.current?.click()}
                disabled={running}
              >
                <Upload className="w-3.5 h-3.5 mr-1" />
                {t('proj.addFiles')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={openFolderPicker}
                disabled={running}
                title={t('proj.addFolderTitle')}
              >
                <FolderUp className="w-3.5 h-3.5 mr-1" />
                {t('proj.addFolder')}
              </Button>
            </div>
            <input
              ref={filesInputRef}
              type="file"
              accept="video/mp4,video/*"
              multiple
              className="hidden"
              onChange={(e) => addFromFileList(e.target.files)}
            />
            <input
              ref={setupFolderInput}
              type="file"
              multiple
              className="hidden"
              onChange={handleFolderInputChange}
            />
          </div>

          {episodes.length === 0 ? (
            <button
              type="button"
              onClick={() => filesInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              className="w-full border-2 border-dashed rounded-md p-8 text-center text-sm text-muted-foreground hover:bg-accent/30 transition-colors"
            >
              <FileVideo className="w-8 h-8 mx-auto mb-2 text-muted-foreground/60" />
              {t('proj.dropHint')}
              <br />
              <span className="text-xs">{t('proj.dropHint2')}</span>
            </button>
          ) : (
            <>
              <div
                className="space-y-1.5 max-h-72 overflow-y-auto pr-1"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
              >
                {episodes.map((ep, idx) => (
                  <EpisodeRow
                    key={`${ep.path || ep.file.name}-${idx}`}
                    index={idx}
                    total={episodes.length}
                    episode={ep}
                    disabled={running}
                    onUp={() => move(idx, -1)}
                    onDown={() => move(idx, 1)}
                    onRemove={() => remove(idx)}
                  />
                ))}
              </div>
              {!running && (
                <div className="mt-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span>{t('proj.needMore')}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-xs h-7 px-2 shrink-0"
                    onClick={openFolderPicker}
                  >
                    <FolderUp className="w-3.5 h-3.5 mr-1" />
                    {t('proj.addMoreFolders')}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        {running && (
          <div className="text-xs text-muted-foreground">
            {t('proj.uploadedOf', completed, episodes.length)}
            {createdProjectId && <> · <code>{createdProjectId.slice(0, 8)}…</code></>}
          </div>
        )}

        {submitError && (
          <div className="text-xs text-destructive border border-destructive/40 rounded-md p-2 bg-destructive/5">
            {submitError}
          </div>
        )}

        {!running && blockers.length > 0 && (
          <div className="text-xs text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-800 rounded-md p-2 bg-amber-50 dark:bg-amber-950/30">
            {t('proj.blockersPrefix', blockers.join(' · '))}
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={running}>
          {t('proj.cancel')}
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          title={!canSubmit && !running ? blockers.join('; ') : undefined}
        >
          {running ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('proj.uploadingN', completed + 1, episodes.length)}
            </>
          ) : (
            t('proj.createAndUpload', String(episodes.length || ''))
          )}
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}

interface EpisodeRowProps {
  index: number
  total: number
  episode: PendingEpisode
  disabled: boolean
  onUp: () => void
  onDown: () => void
  onRemove: () => void
}

function EpisodeRow({ index, total, episode, disabled, onUp, onDown, onRemove }: EpisodeRowProps) {
  const [lang] = useDramaLang()
  const t = dramaT(lang)
  const pct = Math.round(episode.uploaded * 100)
  return (
    <div className="flex items-center gap-2 p-2 rounded-md border bg-card">
      <div className="w-12 text-center">
        <Badge variant="secondary" className="font-mono">
          EP {index + 1}
        </Badge>
      </div>

      <div className="flex flex-col items-center gap-0.5">
        <button
          onClick={onUp}
          disabled={disabled || index === 0}
          className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label={t('proj.moveUp')}
        >
          <ArrowUp className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onDown}
          disabled={disabled || index === total - 1}
          className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label={t('proj.moveDown')}
        >
          <ArrowDown className="w-3.5 h-3.5" />
        </button>
      </div>

      <FileVideo className="w-4 h-4 text-muted-foreground shrink-0" />

      <div className="flex-1 min-w-0">
        <div
          className="text-sm font-medium truncate"
          title={episode.path || episode.file.name}
        >
          {episode.path || episode.file.name}
        </div>
        <div className="text-xs text-muted-foreground">
          {formatSize(episode.file.size)}
          {episode.status === 'failed' && episode.error && (
            <span className="text-destructive ml-2">— {episode.error}</span>
          )}
        </div>
        {episode.status === 'uploading' && (
          <Progress value={pct} className="h-1 mt-1" />
        )}
      </div>

      <div className="w-7 flex justify-center">
        {episode.status === 'done' ? (
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
        ) : episode.status === 'uploading' ? (
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        ) : (
          <button
            onClick={onRemove}
            disabled={disabled}
            className="text-muted-foreground hover:text-destructive disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label={t('proj.removeItem')}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  )
}
